'use client'

import { useEffect, useState } from 'react'
import {
  ensureBrowserAdvertisingConsent,
  readExplicitAdvertisingConsent,
  setBrowserAdvertisingConsent,
} from '@/lib/consent/advertising-consent'

export type AdvertisingConsentChoice = 'implied' | 'express' | 'denied'

export function AdvertisingConsentChoices({
  currentChoice,
  onAllow,
  onDecline,
}: {
  currentChoice: AdvertisingConsentChoice
  onAllow: () => void
  onDecline: () => void
}) {
  const allowed = currentChoice !== 'denied'
  return (
    <section
      id="cookie-choices"
      aria-labelledby="cookie-choices-title"
      style={{
        marginTop: 40,
        padding: 24,
        border: '1px solid var(--line-strong)',
        borderRadius: 'var(--r-1)',
        background: 'var(--surface, #fff)',
      }}
    >
      <h2 id="cookie-choices-title" className="display" style={{ fontSize: 24, margin: 0 }}>
        Cookie choices
      </h2>
      <p style={{ margin: '12px 0 0' }}>
        {allowed
          ? 'Optional cookies are currently allowed.'
          : 'Only essential cookies are currently allowed.'}{' '}
        Optional cookies help us understand which ads bring people to Clear Guard and improve the
        site. Your request works either way.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 18 }}>
        <button
          type="button"
          className="btn btn-primary"
          style={{ minHeight: 48, justifyContent: 'center', flex: '1 1 220px' }}
          onClick={onAllow}
          aria-pressed={currentChoice === 'express'}
        >
          Allow optional cookies
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ minHeight: 48, justifyContent: 'center', flex: '1 1 220px' }}
          onClick={onDecline}
          aria-pressed={currentChoice === 'denied'}
        >
          Use essential only
        </button>
      </div>
    </section>
  )
}

function clearFirstPartyAdvertisingCookies(): void {
  const names = document.cookie
    .split(';')
    .map((part) => part.trim().split('=', 1)[0])
    .filter((name) =>
      /^(?:_ga|_gid|_gcl|_fbp$|_fbc$|_clck$|_clsk$|_uet|__obref$|__oppref$)/.test(name),
    )
  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure`
    document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.clearguard.ca; SameSite=Lax; Secure`
  }
}

export function AdvertisingConsentControls() {
  const [currentChoice, setCurrentChoice] = useState<AdvertisingConsentChoice>('implied')

  useEffect(() => {
    const snapshot = readExplicitAdvertisingConsent(document.cookie) ?? ensureBrowserAdvertisingConsent()
    setCurrentChoice(
      snapshot?.analyticsStorage === 'denied'
        ? 'denied'
        : snapshot?.basis === 'express'
          ? 'express'
          : 'implied',
    )
  }, [])

  const allow = () => {
    setBrowserAdvertisingConsent('granted')
    setCurrentChoice('express')
  }

  const decline = () => {
    setBrowserAdvertisingConsent('denied')
    clearFirstPartyAdvertisingCookies()
    setCurrentChoice('denied')
    window.location.reload()
  }

  return (
    <AdvertisingConsentChoices
      currentChoice={currentChoice}
      onAllow={allow}
      onDecline={decline}
    />
  )
}
